import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Flag, ChevronLeft, ChevronRight, Eraser, Send, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { err, fmtTime } from "./shared";

export default function PrepAttempt() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [idx, setIdx] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const enteredAt = useRef(Date.now());
  const spent = useRef({});
  const submitted = useRef(false);

  useEffect(() => {
    api.get(`/test-prep/attempts/${attemptId}`).then((r) => {
      if (r.data.status !== "in_progress") { navigate(`/prep/result/${attemptId}`, { replace: true }); return; }
      setA(r.data); setRemaining(r.data.remaining_sec);
    }).catch((e) => { toast.error(err(e)); navigate("/prep"); });
  }, [attemptId, navigate]);

  const submit = useCallback(async (auto = false) => {
    if (submitted.current) return;
    submitted.current = true; setSubmitting(true);
    try {
      await api.post(`/test-prep/attempts/${attemptId}/submit`);
      toast.success(auto ? "Time's up — test auto-submitted." : "Test submitted!");
      navigate(`/prep/result/${attemptId}`, { replace: true });
    } catch (e) { toast.error(err(e)); submitted.current = false; setSubmitting(false); }
  }, [attemptId, navigate]);

  useEffect(() => {
    if (!a) return;
    const t = setInterval(() => setRemaining((r) => {
      if (r <= 1) { clearInterval(t); submit(true); return 0; }
      return r - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [a, submit]);

  if (!a) return <div className="p-8 text-slate-400">Loading test...</div>;
  const q = a.questions[idx];
  const answers = a.answers || {};
  const marked = new Set(a.marked || []);

  const save = async (patch) => {
    const timeSpent = (spent.current[q.qid] || 0) + Math.round((Date.now() - enteredAt.current) / 1000);
    spent.current[q.qid] = timeSpent; enteredAt.current = Date.now();
    try { await api.patch(`/test-prep/attempts/${attemptId}/answer`, { question_id: q.qid, time_spent_sec: timeSpent, ...patch }); }
    catch (e) { if (e.response?.status === 409) { toast.info(err(e)); navigate(`/prep/result/${attemptId}`, { replace: true }); } else toast.error("Could not save — check your connection."); }
  };
  const qtype = q.qtype || "single_correct";
  const cur = answers[q.qid];
  const choose = (i) => { setA({ ...a, answers: { ...answers, [q.qid]: i } }); save({ answer: i }); };
  const toggleMulti = (i) => {
    const arr = Array.isArray(cur) ? cur : [];
    const nx = arr.includes(i) ? arr.filter((x) => x !== i) : [...arr, i].sort((x, y) => x - y);
    setA({ ...a, answers: { ...answers, [q.qid]: nx } }); save({ selected: nx });
  };
  const setValue = (v) => {
    const num = v === "" || v == null ? null : Number(v);
    setA({ ...a, answers: { ...answers, [q.qid]: num } }); save({ value: num });
  };
  const clear = () => {
    const n = { ...answers }; delete n[q.qid]; setA({ ...a, answers: n });
    save(qtype === "multi_correct" ? { selected: [] } : qtype === "integer" || qtype === "numerical" ? { value: null } : { answer: null });
  };
  const hasAns = (qq) => { const v = answers[qq.qid]; return v != null && (Array.isArray(v) ? v.length > 0 : true); };
  const toggleMark = () => { const m = !marked.has(q.qid); setA({ ...a, marked: m ? [...marked, q.qid] : [...marked].filter((x) => x !== q.qid) }); save({ marked: m }); };
  const go = (i) => { save({}); setIdx(Math.max(0, Math.min(a.questions.length - 1, i))); };

  const status = (qq) => hasAns(qq) ? (marked.has(qq.qid) ? "bg-violet-400 text-[#041016]" : "bg-emerald-400 text-[#041016]") : marked.has(qq.qid) ? "bg-violet-400/40 text-white" : "bg-white/10 text-slate-300";
  const answered = a.questions.filter(hasAns).length;
  const low = remaining < 60;

  return (
    <div data-testid="prep-attempt" className="mx-auto max-w-6xl px-4 sm:px-8 py-6">
      <div className="sticky top-0 z-10 -mx-4 mb-4 flex items-center justify-between gap-3 border-b border-white/8 bg-[#0B0F17]/95 px-4 py-3 backdrop-blur sm:-mx-8 sm:px-8">
        <div className="min-w-0"><p className="truncate text-sm font-semibold">{a.title}</p><p className="text-[11px] text-slate-500">{a.exam_name} · Q {idx + 1}/{a.questions.length} · {answered} answered</p></div>
        <div className="flex items-center gap-2">
          <span data-testid="attempt-timer" className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 font-mono-data text-sm font-bold ${low ? "animate-pulse border-rose-400/40 bg-rose-400/10 text-rose-300" : "border-cyan-400/30 bg-cyan-400/10 text-cyan-300"}`}><Clock className="h-3.5 w-3.5" /> {fmtTime(remaining)}</span>
          <button data-testid="attempt-submit-btn" onClick={() => setConfirm(true)} disabled={submitting} className="inline-flex items-center gap-1 rounded-full bg-cyan-400 px-4 py-1.5 text-xs font-bold text-[#041016] disabled:opacity-50"><Send className="h-3.5 w-3.5" /> Submit</button>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_260px]">
        <div className="rounded-2xl border border-white/8 bg-[#131C2E] p-5 sm:p-6">
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
            <span className="rounded-full bg-white/5 px-2 py-0.5">{q.subject}</span>{q.chapter && <span className="rounded-full bg-white/5 px-2 py-0.5">{q.chapter}</span>}
            <span className="rounded-full bg-white/5 px-2 py-0.5 capitalize">{q.difficulty}</span><span className="rounded-full bg-white/5 px-2 py-0.5">+{q.marks} / −{q.negative_marks}</span>
            {q.source_type === "sample_practice" && <span className="rounded-full border border-amber-400/20 px-2 py-0.5 text-amber-300">Sample practice question</span>}
          </div>
          <p data-testid="question-text" className="mt-4 text-base leading-relaxed sm:text-lg">{q.n}. {q.text}</p>
          {qtype === "multi_correct" && <p className="mt-2 text-xs text-cyan-300">Select all correct options (one or more).</p>}
          <div className="mt-5 space-y-2">
            {(qtype === "integer" || qtype === "numerical") ? (
              <div>
                <label className="text-xs text-slate-400">Enter your numerical answer</label>
                <input data-testid="numeric-answer-input" type="number" inputMode="decimal"
                  value={cur == null ? "" : cur} onChange={(e) => setValue(e.target.value)}
                  placeholder="e.g. 42"
                  className="mt-1 block w-full max-w-xs rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 text-sm text-white focus:border-cyan-400 focus:outline-none" />
              </div>
            ) : q.options.map((o, i) => {
              const sel = qtype === "multi_correct" ? (Array.isArray(cur) && cur.includes(i)) : cur === i;
              return (
                <button key={i} data-testid={`option-${i}`} onClick={() => (qtype === "multi_correct" ? toggleMulti(i) : choose(i))}
                  className={`flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-left text-sm transition-all ${sel ? "border-cyan-400 bg-cyan-400/10 text-white" : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-white/25"}`}>
                  <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-[11px] font-bold ${qtype === "multi_correct" ? "rounded-md" : "rounded-full"} ${sel ? "bg-cyan-400 text-[#041016]" : "bg-white/10"}`}>{String.fromCharCode(65 + i)}</span>{o}
                </button>
              );
            })}
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-2">
              <button data-testid="mark-review-btn" onClick={toggleMark} className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold ${marked.has(q.qid) ? "border-violet-400/50 bg-violet-400/15 text-violet-300" : "border-white/10 text-slate-300"}`}><Flag className="h-3.5 w-3.5" /> {marked.has(q.qid) ? "Marked" : "Mark for review"}</button>
              <button data-testid="clear-answer-btn" onClick={clear} disabled={answers[q.qid] == null} className="inline-flex items-center gap-1 rounded-full border border-white/10 px-3 py-1.5 text-xs text-slate-300 disabled:opacity-40"><Eraser className="h-3.5 w-3.5" /> Clear</button>
            </div>
            <div className="flex gap-2">
              <button data-testid="prev-question-btn" onClick={() => go(idx - 1)} disabled={idx === 0} className="inline-flex items-center gap-1 rounded-full border border-white/10 px-3 py-1.5 text-xs disabled:opacity-40"><ChevronLeft className="h-3.5 w-3.5" /> Prev</button>
              <button data-testid="next-question-btn" onClick={() => go(idx + 1)} disabled={idx === a.questions.length - 1} className="inline-flex items-center gap-1 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold disabled:opacity-40">Skip / Next <ChevronRight className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        </div>

        <aside className="rounded-2xl border border-white/8 bg-[#131C2E] p-4">
          <p className="text-xs font-semibold text-slate-300">Question palette</p>
          <div data-testid="question-palette" className="mt-3 grid grid-cols-6 gap-1.5 lg:grid-cols-5">
            {a.questions.map((qq, i) => (
              <button key={qq.qid} data-testid={`palette-${i + 1}`} onClick={() => go(i)} className={`h-8 rounded-lg text-xs font-bold transition-all ${status(qq)} ${i === idx ? "ring-2 ring-cyan-400" : ""}`}>{i + 1}</button>
            ))}
          </div>
          <div className="mt-4 space-y-1 text-[11px] text-slate-400">
            <p><span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-400" /> Answered</p>
            <p><span className="inline-block h-2.5 w-2.5 rounded-sm bg-violet-400" /> Marked for review</p>
            <p><span className="inline-block h-2.5 w-2.5 rounded-sm bg-white/10" /> Not answered</p>
          </div>
        </aside>
      </div>

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setConfirm(false)}>
          <div data-testid="submit-confirm-dialog" className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#131C2E] p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold">Submit test?</h3>
            <p className="mt-2 text-sm text-slate-400">{answered} of {a.questions.length} answered · {a.questions.length - answered} unattempted · {marked.size} marked for review.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button data-testid="submit-cancel-btn" onClick={() => setConfirm(false)} className="rounded-full border border-white/10 px-4 py-2 text-xs">Keep going</button>
              <button data-testid="submit-confirm-btn" onClick={() => submit(false)} disabled={submitting} className="inline-flex items-center gap-1 rounded-full bg-cyan-400 px-4 py-2 text-xs font-bold text-[#041016] disabled:opacity-50">{submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Submit now</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
